import {AxiosError} from 'axios'
import {useQuery} from 'react-query'
import {getInvoicePrintPreference} from './printPageRequests'

// Which print page a business's invoices open, print and download with. Every business uses the
// new A4 page (/invoice/{hash}) until it chooses the earlier one (/invoicePrint/{hash}); the
// choice is the business's, made in its settings or in the announcement of the new page.

export {
  getInvoicePrintPageForLink,
  getInvoicePrintPreference,
  saveInvoicePrintPreference,
} from './printPageRequests'

export type InvoicePrintPage = 'new' | 'classic'

export type InvoicePrintPreference = {
  companyId: number
  page: InvoicePrintPage
  /** The user may change the business's page (never a support operator). */
  canChange: boolean
  /**
   * Choices can be stored. Until the server's database has the setting table every business uses
   * the new page, no choice is offered and the announcement waits.
   */
  available: boolean
  /** The business's newest invoice, to see it with either page; null when it has none. */
  latestInvoice: {
    hashCode: string
    number: string | null
    issuedAt: string
    amount: string
  } | null
}

export const PRINT_PAGE_TITLES: Record<InvoicePrintPage, string> = {
  new: 'صفحهٔ چاپ جدید',
  classic: 'صفحهٔ چاپ قدیم',
}

export const otherPrintPage = (page: InvoicePrintPage): InvoicePrintPage =>
  page === 'new' ? 'classic' : 'new'

/** The A4 print page; it opens without signing in. */
export const newPrintPageUrl = (hash: string) => `/invoice/${hash}`

/** The earlier print page, kept from following the business's choice. */
export const classicPrintPageUrl = (hash: string) => `/invoicePrint/${hash}?page=classic`

/**
 * The page an invoice opens with: the given one, or, when the business's choice is not known
 * here (an administrator's list of every business), the link that follows the invoice's own
 * business.
 */
export const printPageUrl = (hash: string, page?: InvoicePrintPage | null) =>
  page === 'new'
    ? newPrintPageUrl(hash)
    : page === 'classic'
    ? classicPrintPageUrl(hash)
    : `/invoicePrint/${hash}`

export const printPreferenceQueryKey = (companyId: number) => [
  'invoice-print-preference',
  companyId,
]

/** The business's print page; one read per business and session, shared by every invoice row. */
export const useInvoicePrintPreference = (companyId: number, enabled = true) =>
  useQuery(
    printPreferenceQueryKey(companyId),
    ({signal}) => getInvoicePrintPreference(companyId, signal),
    {
      enabled: enabled && companyId > 0,
      staleTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: false,
    }
  )

export const readPrintPreferenceError = (error: unknown, fallback: string) => {
  const message = (error as AxiosError<{message?: string}>)?.response?.data?.message
  return typeof message === 'string' && message.trim() ? message : fallback
}
